import React, { useState, useEffect, useCallback } from "react";
import {
  Page, Layout, Card, Select, Banner, Spinner, Text, Badge, InlineStack, Checkbox,
} from "@shopify/polaris";
import { useShop } from "../context/ShopContext.jsx";
import { useI18n } from "../context/I18nContext.jsx";
import { SUPPORTED_LANGUAGES } from "../i18n/translations.js";

export default function SettingsPage() {
  const { apiFetch, shopDomain } = useShop();
  const { t, lang, setLanguage } = useI18n();
  const [selectedLang, setSelectedLang] = useState(lang);
  const [customerLang, setCustomerLang] = useState("nl");
  const [sizeAdviceEnabled, setSizeAdviceEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    setLoading(true);
    apiFetch("/settings")
      .then((data) => {
        if (data?.language) setSelectedLang(data.language);
        if (data?.customerLanguage) setCustomerLang(data.customerLanguage);
        setSizeAdviceEnabled(data?.sizeAdviceEnabled !== false);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [apiFetch, shopDomain]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      await apiFetch("/settings", {
        method: "PUT",
        body: JSON.stringify({ language: selectedLang, customerLanguage: customerLang, sizeAdviceEnabled }),
      });
      setLanguage(selectedLang);
      setToast("success");
    } catch {
      setToast("error");
    } finally {
      setSaving(false);
    }
  }, [apiFetch, selectedLang, customerLang, sizeAdviceEnabled, setLanguage]);

  if (loading) {
    return (
      <Page title={t("settings.title")}>
        <div style={{ display: "flex", justifyContent: "center", padding: 60 }}>
          <Spinner size="large" />
        </div>
      </Page>
    );
  }

  return (
    <Page
      title={t("settings.title")}
      subtitle={t("settings.subtitle")}
      primaryAction={{ content: saving ? t("common.saving") : t("common.save"), onAction: handleSave, loading: saving }}
    >
      <Layout>
        {toast === "success" && (
          <Layout.Section>
            <Banner tone="success" onDismiss={() => setToast(null)}>
              {t("settings.saved")}
            </Banner>
          </Layout.Section>
        )}
        {toast === "error" && (
          <Layout.Section>
            <Banner tone="critical" onDismiss={() => setToast(null)}>
              {t("settings.saveFailed")}
            </Banner>
          </Layout.Section>
        )}

        <Layout.AnnotatedSection
          title={t("settings.language")}
          description={t("settings.languageHelp")}
        >
          <Card>
            {shopDomain && (
              <div style={{ marginBottom: 12 }}>
                <InlineStack gap="200" blockAlign="center">
                  <Text variant="bodySm" tone="subdued">{t("settings.shopLabel")}</Text>
                  <Badge>{shopDomain.replace(".myshopify.com", "")}</Badge>
                </InlineStack>
              </div>
            )}
            <Select
              label={t("settings.dashboardLanguage")}
              options={SUPPORTED_LANGUAGES}
              value={selectedLang}
              onChange={setSelectedLang}
              helpText={t("settings.dashboardLanguageHelp")}
            />
            <div style={{ marginTop: 16 }}>
              <Select
                label={t("settings.customerLanguage")}
                options={SUPPORTED_LANGUAGES}
                value={customerLang}
                onChange={setCustomerLang}
                helpText={t("settings.customerLanguageHelp")}
              />
            </div>
          </Card>
        </Layout.AnnotatedSection>

        <Layout.AnnotatedSection
          title={t("settings.sizeAdviceTitle")}
          description={t("settings.sizeAdviceDescription")}
        >
          <Card>
            <Checkbox
              label={t("settings.sizeAdviceToggle")}
              checked={sizeAdviceEnabled}
              onChange={setSizeAdviceEnabled}
              helpText={t("settings.sizeAdviceToggleHelp")}
            />
            {!sizeAdviceEnabled && (
              <div style={{ marginTop: 12 }}>
                <Banner tone="warning">{t("settings.sizeAdviceOffWarning")}</Banner>
              </div>
            )}
          </Card>
        </Layout.AnnotatedSection>
      </Layout>
    </Page>
  );
}
